// Plan-of-care lifecycle helpers — the single source of truth for which plans
// are "live", which plan currently governs a case, and the derived workflow
// facts (sent to physician, physician signed, certification expiry).
//
// Stored status is lifecycle only: 'draft' | 'active' | 'discontinued'.
// 'expired', 'sent_to_physician', and 'physician_signed' are legacy stored
// values — nothing writes them anymore; they are tolerated on read until the
// one-time backfill maps them away (see backfill-poc-statuses.mjs).

interface PocLike {
  id?: string;
  case_id?: number | null;
  status?: string;
  certification_start?: number | null;
  certification_end?: number | null;
  created_at?: number | null;
  sent_to_physician_at?: number | null;
  physician_signed_at?: number | null;
  signed_document?: unknown;
}

export const POC_STATUSES = ['draft', 'active', 'discontinued'] as const;

// 'expired' stays in the exclusion set permanently as a cheap guard against
// legacy rows; it is never written.
export const NON_LIVE_POC_STATUSES = ['draft', 'discontinued', 'expired'];

export function isLivePocStatus(status?: string | null): boolean {
  return !NON_LIVE_POC_STATUSES.includes(status || '');
}

export function isLivePoc(poc?: PocLike | null): boolean {
  return !!poc && isLivePocStatus(poc.status);
}

// Xano timestamps arrive in seconds or milliseconds depending on the path.
function toMs(ts?: number | null): number | null {
  if (!ts) return null;
  return ts > 1e12 ? ts : ts * 1000;
}

function comparePocsDesc(a: PocLike, b: PocLike): number {
  return (
    (b.certification_start || 0) - (a.certification_start || 0) ||
    (b.created_at || 0) - (a.created_at || 0)
  );
}

/**
 * The plan whose certification currently governs a case: among live plans,
 * newest certification_start, tiebreak newest created_at. An older row still
 * stamped active (imported data, pre-backfill rows) must not mask or
 * misreport the current certification.
 *
 * opts.requireCertEnd: only consider plans that have a certification_end —
 * expiration-alert callers pass true so a plan without a cert window can
 * never drive (or suppress) an expiration alert.
 */
export function selectCurrentPoc<T extends PocLike>(
  pocs?: T[] | null,
  opts?: { requireCertEnd?: boolean }
): T | null {
  const live = (pocs || []).filter(
    (p) => isLivePoc(p) && (!opts?.requireCertEnd || p.certification_end)
  );
  if (!live.length) return null;
  return [...live].sort(comparePocsDesc)[0];
}

// Derived workflow chips. The status fallbacks keep pre-backfill rows
// rendering correctly; remove them once the backfill verifies clean.
export function isPocSentToPhysician(poc?: PocLike | null): boolean {
  if (!poc) return false;
  return (
    !!poc.sent_to_physician_at ||
    poc.status === 'sent_to_physician' ||
    poc.status === 'physician_signed'
  );
}

export function isPocPhysicianSigned(poc?: PocLike | null): boolean {
  if (!poc) return false;
  return (
    !!poc.physician_signed_at ||
    !!poc.signed_document ||
    poc.status === 'physician_signed'
  );
}

/** Whole days until certification_end (negative = lapsed); null when no cert end. */
export function getPocDaysUntilExpiry(
  poc?: PocLike | null,
  nowMs: number = Date.now()
): number | null {
  const endMs = toMs(poc?.certification_end);
  if (endMs == null) return null;
  return Math.ceil((endMs - nowMs) / 86400000);
}

/** A live sibling plan with a newer certification period exists. */
export function isPocSuperseded(
  poc?: PocLike | null,
  siblingPocs?: PocLike[] | null
): boolean {
  if (!poc || !isLivePoc(poc)) return false;
  return (siblingPocs || []).some(
    (s) => s.id !== poc.id && isLivePoc(s) && comparePocsDesc(poc, s) > 0
  );
}
